import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Flight } from '../../types/Quotation';
import AsyncSelect from 'react-select/async';
import { LocationOption } from '../../types/Location';
import { loadAirportLocations } from '../../api/Location';
import { useFlights } from '../../hooks/useFlights';
import '../../css/FlightListModal.css';

interface FlightListModalProps {
  startDate: string;
  from?: LocationOption;
  to?: LocationOption;
  travellers?: { adults: number; children: number; infants: number };
  // Prefill helpers
  roundTripDefault?: boolean;
  returnDate?: string;
  defaultToCityLabel?: string; // e.g., first destination city like "Bangkok"
  onClose: () => void;
  onSelectOutbound: (flight: Flight) => void;
  onSelectReturn: (flight: Flight) => void;
}

const FlightListModal: React.FC<FlightListModalProps> = ({
  startDate,
  from: defaultFrom,
  to: defaultTo,
  travellers,
  roundTripDefault,
  returnDate,
  defaultToCityLabel,
  onClose,
  onSelectOutbound,
  onSelectReturn,
}) => {
  const [fromOption, setFromOption] = useState<LocationOption | undefined>(defaultFrom);
  const [toOption, setToOption] = useState<LocationOption | undefined>(defaultTo);
  const [depDate, setDepDate] = useState(startDate);
  const [isRoundTrip, setIsRoundTrip] = useState<boolean>(!!roundTripDefault);
  const [retDate, setRetDate] = useState(returnDate || startDate);
  const [sortBy, setSortBy] = useState<'price' | 'departure' | 'arrival' | 'duration'>('price');
  const [stopsFilter, setStopsFilter] = useState<'any' | '0' | '1' | '2+'>('any');
  const [cabinClass, setCabinClass] = useState<number>(1); // Default to 1 (All)
  const [outboundAdded, setOutboundAdded] = useState<boolean>(false);

  useEffect(() => {
    setDepDate(startDate);
  }, [startDate]);

  useEffect(() => {
    if (returnDate) setRetDate(returnDate);
  }, [returnDate]);

  // Attempt to prefill destination airport based on a city label when not provided
  useEffect(() => {
    let cancelled = false;
    if (!toOption && defaultToCityLabel && defaultToCityLabel.trim().length >= 3) {
      loadAirportLocations(defaultToCityLabel).then((opts) => {
        if (cancelled) return;
        const lower = defaultToCityLabel.toLowerCase();
        const match = (opts || []).find(o => o.label.toLowerCase().includes(lower)) || (opts || [])[0];
        if (match) setToOption(match);
      }).catch(() => void 0);
    }
    return () => { cancelled = true; };
  }, [defaultToCityLabel, toOption]);

  const { flights, loading } = useFlights(fromOption ? fromOption.value: '', toOption ? toOption.value : '', depDate, travellers, cabinClass);
  const { flights: returnFlights, loading: loadingReturn } = useFlights(
    isRoundTrip ? (toOption ? toOption.value : '') : '',
    isRoundTrip ? (fromOption ? fromOption.value : '') : '',
    isRoundTrip ? retDate : '',
    travellers,
    cabinClass
  );

  const filterByStops = useCallback((arr: Flight[]) => arr.filter(f => {
    if (stopsFilter === 'any') return true;
    if (stopsFilter === '0') return f.stops === 0;
    if (stopsFilter === '1') return f.stops === 1;
    return f.stops >= 2;
  }), [stopsFilter]);

  const parseDur = (d: string) => {
    const h = /([0-9]+)h/.exec(d)?.[1];
    const m = /([0-9]+)m/.exec(d)?.[1];
    return (h ? parseInt(h, 10) * 60 : 0) + (m ? parseInt(m, 10) : 0);
  };

  const sortFlights = useCallback((arr: Flight[]) => {
    switch (sortBy) {
      case 'price':
        return [...arr].sort((a, b) => a.price - b.price);
      case 'departure':
        return [...arr].sort((a, b) => +new Date(a.departureTime) - +new Date(b.departureTime));
      case 'arrival':
        return [...arr].sort((a, b) => +new Date(a.arrivalTime) - +new Date(b.arrivalTime));
      case 'duration':
        return [...arr].sort((a, b) => parseDur(a.duration) - parseDur(b.duration));
      default:
        return arr;
    }
  }, [sortBy]);

  const visibleFlights = useMemo(
    () => sortFlights(filterByStops(flights)),
    [flights, filterByStops, sortFlights]
  );
  
  const visibleReturnFlights = useMemo(() => {
    if (!isRoundTrip) return [] as typeof flights;
    return sortFlights(filterByStops(returnFlights));
  }, [returnFlights, isRoundTrip, filterByStops, sortFlights]);

  const fmtTime = (dt: string) =>
    new Date(dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="modal-backdrop">
      <div className="modal flight-modal">
        <button className="close-btn" onClick={onClose}>×</button>

        <div className="search-form">
          <div className="sf-row sf-row-1">
            <div className="sf-field">
              <label>Trip</label>
              <select
                value={isRoundTrip ? 'roundtrip' : 'oneway'}
                onChange={e => setIsRoundTrip(e.target.value === 'roundtrip')}
              >
                <option value="oneway">One Way</option>
                <option value="roundtrip">Round Trip</option>
              </select>
            </div>
            <div className="sf-field">
              <label>From</label>
              <AsyncSelect<LocationOption, false>
                cacheOptions
                defaultOptions
                loadOptions={loadAirportLocations}
                getOptionLabel={o => o.label}
                getOptionValue={o => o.value}
                onChange={opt => setFromOption(opt as LocationOption)}
                value={fromOption}
                placeholder="Type or select an airport"
                classNamePrefix="rs"
              />
            </div>
            <div className="sf-field">
              <label>To</label>
              <AsyncSelect<LocationOption, false>
                cacheOptions
                defaultOptions
                loadOptions={loadAirportLocations}
                getOptionLabel={o => o.label}
                getOptionValue={o => o.value}
                onChange={opt => setToOption(opt as LocationOption)}
                value={toOption}
                placeholder="Type or select an airport"
                classNamePrefix="rs"
              />
            </div>
          </div>
          <div className="sf-row sf-row-2">
            <div className="sf-field">
              <label>Onward Date</label>
              <input
                type="date"
                value={depDate}
                onChange={e => setDepDate(e.target.value)}
              />
            </div>
            <div className="sf-field">
              <label>Return Date</label>
              <input
                type="date"
                value={retDate}
                onChange={e => setRetDate(e.target.value)}
                disabled={!isRoundTrip}
              />
            </div>
            <div className="sf-actions">
              <button className="sf-search-btn">Search</button>
            </div>
          </div>
          <div className="sf-row sf-row-3">
            <div className="sf-field">
              <label>Cabin Class</label>
              <div className="cabin-class-options">
                <label className="cabin-option">
                  <input
                    type="radio"
                    name="cabinClass"
                    value="1"
                    checked={cabinClass === 1}
                    onChange={e => setCabinClass(Number(e.target.value))}
                  />
                  <span>All</span>
                </label>
                <label className="cabin-option">
                  <input
                    type="radio"
                    name="cabinClass"
                    value="2"
                    checked={cabinClass === 2}
                    onChange={e => setCabinClass(Number(e.target.value))}
                  />
                  <span>Economy</span>
                </label>
                <label className="cabin-option">
                  <input
                    type="radio"
                    name="cabinClass"
                    value="3"
                    checked={cabinClass === 3}
                    onChange={e => setCabinClass(Number(e.target.value))}
                  />
                  <span>Premium Economy</span>
                </label>
                <label className="cabin-option">
                  <input
                    type="radio"
                    name="cabinClass"
                    value="4"
                    checked={cabinClass === 4}
                    onChange={e => setCabinClass(Number(e.target.value))}
                  />
                  <span>Business</span>
                </label>
                <label className="cabin-option">
                  <input
                    type="radio"
                    name="cabinClass"
                    value="5"
                    checked={cabinClass === 5}
                    onChange={e => setCabinClass(Number(e.target.value))}
                  />
                  <span>Premium Business</span>
                </label>
                <label className="cabin-option">
                  <input
                    type="radio"
                    name="cabinClass"
                    value="6"
                    checked={cabinClass === 6}
                    onChange={e => setCabinClass(Number(e.target.value))}
                  />
                  <span>First</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="list-header">
          <div className="sort-buttons">
            <button className={sortBy === 'price' ? 'active' : ''} onClick={() => setSortBy('price')}>Price</button>
            <button className={sortBy === 'departure' ? 'active' : ''} onClick={() => setSortBy('departure')}>Departure</button>
            <button className={sortBy === 'arrival' ? 'active' : ''} onClick={() => setSortBy('arrival')}>Arrival</button>
            <button className={sortBy === 'duration' ? 'active' : ''} onClick={() => setSortBy('duration')}>Duration</button>
          </div>
          <div className="stops-filter">
            <label style={{ fontSize: 12, color: '#666', marginRight: 6 }}>Stops</label>
            <select value={stopsFilter} onChange={e => setStopsFilter(e.target.value as any)}>
              <option value="any">Any</option>
              <option value="0">Non-stop</option>
              <option value="1">1 stop</option>
              <option value="2+">2+ stops</option>
            </select>
          </div>
        </div>

        <div className="flight-list">
          {loading && <p className="loading">Loading…</p>}
          {!loading && visibleFlights.map((f, i) => (
            <div className="flight-card" key={i}>
              <div className="flight-main">
                <div className="flight-top">
                  <div className="airline-info">
                    <img
                      src={`https://cdn.otaprime.com/airline/${f.provider}.gif`}
                      alt={f.airline}
                      className="airline-logo"
                    />
                    <span className="airline-name">{f.airline}</span>
                  </div>
                  <div className="flight-number">{f.provider}-{f.flightNumber}</div>
                </div>
                <div className="flight-times">
                  <span className="time">{fmtTime(f.departureTime)}</span>
                  <span className="timeline-icon">✈️</span>
                  <span className="time">{fmtTime(f.arrivalTime)}</span>
                </div>
                <div className="flight-bottom">
                  <span className="badge">Free Cabin 8kg</span>
                  <span className="price">₹{f.price.toLocaleString('en-IN')}</span>
                </div>
                {/* Brief segment + layover details */}
                {Array.isArray((f as any).segments) && (f as any).segments.length > 0 && (
                  <div className="segment-brief" style={{ marginTop: 6, fontSize: 12, color: '#555' }}>
                    <div>
                      Segments: {((f as any).segments as any[]).map((s: any, idx: number) => {
                        const mins = typeof s.durationMins === 'number' ? s.durationMins : 0;
                        const h = Math.floor(mins / 60);
                        const m = mins % 60;
                        return `${s.fromCode || s.origin}-${s.toCode || s.destination} (${h}h${m ? ' ' + m + 'm' : ''})`;
                      }).join(', ')}
                    </div>
                    {((f as any).segments as any[]).length > 1 && (
                      <div>
                        Layovers: {((f as any).segments as any[]).slice(0, -1).map((s: any, idx: number) => {
                          const next = (f as any).segments[idx + 1];
                          const arrive = new Date(s.arrivalTime);
                          const depart = new Date(next.departureTime);
                          const diffMins = Math.max(0, Math.round((+depart - +arrive) / 60000));
                          const h = Math.floor(diffMins / 60);
                          const m = diffMins % 60;
                          const code = s.toCode || s.destination;
                          return `${code} ${h}h${m ? ' ' + m + 'm' : ''}`;
                        }).join(', ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="card-right">
                <button
                  className="select-btn"
                  onClick={() => {
                    onSelectOutbound({
                      ...f,
                      from: fromOption?.label || f.from,
                      to: toOption?.label || f.to,
                    });
                    setOutboundAdded(true);
                    if (!isRoundTrip) onClose();
                  }}
                >
                  {isRoundTrip ? (outboundAdded ? 'Selected' : 'Select') : 'Select'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {isRoundTrip && (
          <>
            <div className="list-header" style={{ marginTop: 16 }}>
              <div className="sort-buttons" />
              <p>{visibleReturnFlights.length} return results found</p>
            </div>
            <div className="flight-list">
              {loadingReturn && <p className="loading">Loading…</p>}
              {!loadingReturn && visibleReturnFlights.map((f, i) => (
                <div className="flight-card" key={`ret-${i}`}>
                  <div className="flight-main">
                    <div className="flight-top">
                      <div className="airline-info">
                        <img
                          src={`https://cdn.otaprime.com/airline/${f.provider}.gif`}
                          alt={f.airline}
                          className="airline-logo"
                        />
                        <span className="airline-name">{f.airline}</span>
                      </div>
                      <div className="flight-number">{f.provider}-{f.flightNumber}</div>
                    </div>
                    <div className="flight-times">
                      <span className="time">{fmtTime(f.departureTime)}</span>
                      <span className="timeline-icon">✈️</span>
                      <span className="time">{fmtTime(f.arrivalTime)}</span>
                    </div>
                    <div className="flight-bottom">
                      <span className="badge">Free Cabin 8kg</span>
                      <span className="price">₹{f.price.toLocaleString('en-IN')}</span>
                    </div>
                    {Array.isArray((f as any).segments) && (f as any).segments.length > 0 && (
                      <div className="segment-brief" style={{ marginTop: 6, fontSize: 12, color: '#555' }}>
                        <div>
                          Segments: {((f as any).segments as any[]).map((s: any) => {
                            const mins = typeof s.durationMins === 'number' ? s.durationMins : 0;
                            const h = Math.floor(mins / 60);
                            const m = mins % 60;
                            return `${s.fromCode || s.origin}-${s.toCode || s.destination} (${h}h${m ? ' ' + m + 'm' : ''})`;
                          }).join(', ')}
                        </div>
                        {((f as any).segments as any[]).length > 1 && (
                          <div>
                            Layovers: {((f as any).segments as any[]).slice(0, -1).map((s: any, idx: number) => {
                              const next = (f as any).segments[idx + 1];
                              const arrive = new Date(s.arrivalTime);
                              const depart = new Date(next.departureTime);
                              const diffMins = Math.max(0, Math.round((+depart - +arrive) / 60000));
                              const h = Math.floor(diffMins / 60);
                              const m = diffMins % 60;
                              const code = s.toCode || s.destination;
                              return `${code} ${h}h${m ? ' ' + m + 'm' : ''}`;
                            }).join(', ')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="card-right">
                    <button
                      className="select-btn"
                      disabled={!outboundAdded}
                      onClick={() => {
                        onSelectReturn({
                          ...f,
                          from: toOption?.label || f.from,
                          to: fromOption?.label || f.to,
                        });
                        onClose();
                      }}
                    >
                      {outboundAdded ? 'Select Return' : 'Select outbound first'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FlightListModal;
